import {
  Box,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import { getProgressColor } from "@/modules/agents/hooks/agents-targets-mapping.hook";
import { getEffectiveTargetTypeColor } from "../types";
import MedalStar from "@/assets/icons/agents-targets/icons/medal-start";
import { UserType, TargetUserDTO } from "@/modules/agents/dtos/index.v2";
import { useTargetCard } from "../hooks/target-card.hook";
import MoreOptionsDropdown from "./more-options-dropdown";

interface targetsData {
  period: string;
  current: string;
  target: string;
  percentage: number;
  effectiveTargetType?: string;
  targetType?: "team" | "personal";
  extraAchievement?: number;
  userType?: UserType;
}

interface HeadOfSalesCardProps {
  name?: string;
  targetsData?: targetsData[];
  user?: TargetUserDTO;
  quarter?: 1 | 2 | 3 | 4;
  year?: number;
}

export default function TargetCard({
  name,
  targetsData,
  user,
  quarter,
  year,
}: HeadOfSalesCardProps) {
  const theme = useTheme();

  const {
    cardRef,
    cardWidth,
    progressBarWidth,
    hasTeamTarget,
    hasPersonalTarget,
    allPeriods,
    getRowsToShow,
    getUserTypeChipColors,
  } = useTargetCard({ targetsData });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        gap: theme.nawy.spacing.lg,
        paddingX: theme.nawy.spacing.md,
        borderRadius: theme.nawy.radius.md,
        minWidth: `${cardWidth}px`,
        maxHeight: "0px",
      }}
      ref={cardRef}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.xs,
          minWidth: "180px",
        }}
      >
        <Box>
          <Typography
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.md.semibold,
              maxWidth: "140px",
              wordWrap: "break-word",
              lineHeight: 1.2,
            }}
          >
            {name}
          </Typography>
          {hasPersonalTarget && hasTeamTarget && (
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.regular,
                mt: 6,
                ml: 3,
              }}
            >
              Personal Target
            </Typography>
          )}
        </Box>
      </Box>

      {user && quarter && year ? (
        <MoreOptionsDropdown user={user} quarter={quarter} year={year} />
      ) : (
        <IconButton
          size="small"
          sx={{
            color: theme.nawy.colors.semantic.text.tertiary,
            marginRight: theme.nawy.spacing.md,
          }}
        >
          <MoreHoriz />
        </IconButton>
      )}

      {/* Sales data columns */}
      {(() => {
        const renderRow = (d: targetsData) => (
          <Box
            key={`${d.period}-${d.targetType || "team"}`}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Period header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: theme.nawy.spacing.xs,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    color: theme.nawy.colors.semantic.text.tertiary,
                    textAlign: "center",
                    ...theme.nawy.typography.english.variants.body.sm.semibold,
                  }}
                >
                  {d.period}
                </Typography>
                {d.extraAchievement ? (
                  <Typography
                    sx={{
                      textAlign: "center",
                      ...theme.nawy.typography.english.variants.body.sm
                        .semibold,
                      padding: "4px 8px",
                      // borderRadius: theme.nawy.radius.md,
                      // backgroundColor: theme.nawy.colors.primitives.green[50],
                      color: theme.nawy.colors.primitives.green[500],
                      ml: 1,
                    }}
                  >
                    Ext: {d.extraAchievement.toLocaleString()} EGP
                  </Typography>
                ) : (
                  ""
                )}
              </Box>
            </Box>
            {/* Sales figures */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: theme.nawy.spacing.xs,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.semibold,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {Number(d.current.replace(/,/g, "")).toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </Typography>
                <Typography
                  sx={{
                    color: theme.nawy.colors.semantic.text.secondary,
                  }}
                >
                  /
                </Typography>
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.secondary,
                  }}
                >
                  {Number(d.target.replace(/,/g, "")).toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}{" "}
                  EGP
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: getEffectiveTargetTypeColor(
                      d.effectiveTargetType || "normal"
                    ),
                    textAlign: "center",
                    "&:hover": {
                      color: theme.nawy.colors.primitives.blue[600],
                    },
                    ...theme.nawy.typography.english.variants.body.xxs.regular,
                  }}
                >
                  {d.effectiveTargetType ? d.effectiveTargetType : ""}
                </Typography>
              </Box>
            </Box>
            {/* Progress bar */}
            <Box
              sx={{
                width: `${progressBarWidth}px`,
                minWidth: "200px",
                position: "relative",
              }}
            >
              <LinearProgress
                value={Math.min(d.percentage, 100)}
                variant="determinate"
                sx={{
                  height: theme.nawy.spacing.xxs,
                  borderRadius: theme.nawy.radius.lg,
                  backgroundColor: theme.nawy.colors.semantic.border.secondary,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: getProgressColor(d.percentage / 100),
                    borderRadius: theme.nawy.radius.lg,
                  },
                }}
              />

              <Box
                sx={{
                  marginLeft: theme.nawy.spacing.xxs,
                  display: "flex",
                  alignItems: "center",
                  marginTop: theme.nawy.spacing.xxs,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: theme.nawy.spacing.xxs,
                  }}
                >
                  {d.percentage >= 100 && (
                    <MedalStar
                      size={16}
                      sx={{
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      color: getProgressColor(d.percentage / 100),
                      ...theme.nawy.typography.english.variants.body.xs
                        .semibold,
                    }}
                  >
                    {d.percentage === Infinity ? "100%" : `${d.percentage}%`}
                  </Typography>
                </Box>
                <Box>
                  {d.userType && d.userType !== UserType.NORMAL && (
                    <Chip
                      label={d.userType}
                      size="small"
                      sx={{
                        backgroundColor: getUserTypeChipColors(d.userType)
                          .backgroundColor,
                        color: getUserTypeChipColors(d.userType).textColor,
                        borderRadius: theme.nawy.radius.md,
                        height: theme.nawy.spacing.md,
                        textTransform: "capitalize",
                        ...theme.nawy.typography.english.variants.body.xs
                          .regular,
                        marginTop: theme.nawy.spacing.xxs,
                        "& .MuiChip-label": {
                          padding: `0 ${theme.nawy.spacing.xxs}`,
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        );

        return allPeriods.map((period) => {
          const rowsToShow = getRowsToShow(period);

          return (
            <Box
              key={`col-${period}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.md,
              }}
            >
              {rowsToShow.map(renderRow)}
            </Box>
          );
        });
      })()}
    </Box>
  );
}
